import { Button, Input, Modal, Switch, Tooltip } from "rizzui";
import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { SpecieService } from "../../../../services/specie/specieService";
import { toast } from "sonner";
import * as yup from 'yup';
import RequiredInput from "../../../../components/forms/InputHOC";
import { CiCircleInfo } from "react-icons/ci";
import CustomSelect from "../../../../components/forms/CustomSelect";
import { speciesGroupSelector, riskLevelOptions } from "../../../../utils/SelectorMapping";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import useCompanies from "../../../../hooks/api-calls/useCompanies";


interface SpeciesModalProps {
    specie?: any;
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
}

const schema = yup.object({

    commonName: yup.string().required('Campo requerido'),
    scientificName: yup.string().required('Campo requerido'),
    family: yup.string().required('Campo requerido'),
    specieIdentifier: yup.string().required('Campo requerido'),
    acronym: yup.string().required('Campo requerido'),
    infoPage: yup.string().nullable(),
    censusAvailable: yup.boolean(),
    ofcAvailable: yup.boolean(),
    issueAvailable: yup.boolean(),
    carrionAvailable: yup.boolean(),
    preventiveStopsAvailable: yup.boolean(),
    catAndalucia: yup.boolean(),
    catNacional: yup.boolean(),
    riskLevelAndalucia: yup.string().nullable().when('catAndalucia', {
        is: true,
        then: (schema) => schema.required('El nivel de riesgo de Andalucía es obligatorio cuando la especie está amenazada a nivel andaluz'),
        otherwise: (schema) => schema.nullable()
    }),
    riskLevelNacional: yup.string().nullable().when('catNacional', {
        is: true,
        then: (schema) => schema.required('El nivel de riesgo nacional es obligatorio cuando la especie está amenazada a nivel nacional'),
        otherwise: (schema) => schema.nullable()
    }),
});


export const SpeciesModal: React.FC<SpeciesModalProps> = ({ specie = null, isOpen, onClose, refetch }) => {

    // STATES

    const isEdit = specie !== null;
    const [loading, setLoading] = useState(false);

    // HOOKS
    const { user } = useSelector((state: RootState) => state.auth);
    const isSuperAdmin = !!user?.roles?.includes('Superadministrador');
    const { getCompaniesList } = useCompanies();
    const schema = useMemo(() => {
        return yup.object({
            company: isSuperAdmin ? yup.string().required('Campo requerido') : yup.string().nullable().notRequired(),
            commonName: yup.string().required('Campo requerido'),
            scientificName: yup.string().required('Campo requerido'),
            family: yup.string().required('Campo requerido'),
            specieIdentifier: yup.string().required('Campo requerido'),
            acronym: yup.string().required('Campo requerido'),
            infoPage: yup.string().nullable(),
            censusAvailable: yup.boolean(),
            ofcAvailable: yup.boolean(),
            issueAvailable: yup.boolean(),
            catAndalucia: yup.boolean(),
            catNacional: yup.boolean(),
            riskLevelAndalucia: yup.string().nullable().when('catAndalucia', {
                is: true,
                then: (schema) => schema.required('El nivel de riesgo de Andalucía es obligatorio cuando la especie está amenazada a nivel andaluz'),
                otherwise: (schema) => schema.nullable()
            }),
            riskLevelNacional: yup.string().nullable().when('catNacional', {
                is: true,
                then: (schema) => schema.required('El nivel de riesgo nacional es obligatorio cuando la especie está amenazada a nivel nacional'),
                otherwise: (schema) => schema.nullable()
            }),
        });
    }, [isSuperAdmin]);

    // FUNCTIONS 

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR NUEVA ESPECIE
     * @EN CREATE NEW SPECIE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        setLoading(true);
        try {
            const formData = new FormData();
            if (isSuperAdmin) {
                formData.append('company', values.company?.toString() || "");
            }
            formData.append('commonName', values.commonName);
            formData.append('scientificName', values.scientificName);
            formData.append('family', values.family);
            formData.append('specieIdentifier', values.specieIdentifier);
            formData.append('acronym', values.acronym);
            formData.append('censusAvailable', values.censusAvailable);
            formData.append('ofcAvailable', values.ofcAvailable);
            formData.append('issueAvailable', values.issueAvailable);
            formData.append('carrionAvailable', values.carrionAvailable);
            formData.append('preventiveStopsAvailable', values.preventiveStopsAvailable);
            formData.append('catAndalucia', values.catAndalucia);
            if (values.catAndalucia && values.riskLevelAndalucia) {
                formData.append('riskLevelAndalucia', values.riskLevelAndalucia);
            }
            formData.append('catNacional', values.catNacional);
            if (values.catNacional && values.riskLevelNacional) {
                formData.append('riskLevelNacional', values.riskLevelNacional);
            }
            if (values.infoPage && values.infoPage !== '') {
                formData.append('infoPage', values.infoPage);
            }
            if (values.document) {
                formData.append('document', values.document);
            }
            const response = await (await (new SpecieService()).createSpecie(formData)).getResponseData();
            if (response.success) {
                onClose();
                refetch();
                toast.success('Especie creada correctamente');
            } else {
                toast.error('Error creando especie:', response.message);
                onClose();
                console.error('Error creating specie:', response.message);
            }
        }
        catch (error) {
            console.error('Error creating species:', error);
        } finally {
            setLoading(false);
        }
    }
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ACTUALIZAR ESPECIE
     * @EN UPDATE SPECIE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleUpdate = async (values: any) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('id', specie.id);
            if (isSuperAdmin) {
                formData.append('company', values.company?.toString() || "");
            }
            formData.append('commonName', values.commonName);
            formData.append('scientificName', values.scientificName);
            formData.append('family', values.family);
            formData.append('specieIdentifier', values.specieIdentifier);
            formData.append('acronym', values.acronym);
            formData.append('censusAvailable', values.censusAvailable);
            formData.append('ofcAvailable', values.ofcAvailable);
            formData.append('issueAvailable', values.issueAvailable);
            formData.append('carrionAvailable', values.carrionAvailable);
            formData.append('preventiveStopsAvailable', values.preventiveStopsAvailable);
            formData.append('catAndalucia', values.catAndalucia);
            if (values.catAndalucia && values.riskLevelAndalucia) {
                formData.append('riskLevelAndalucia', values.riskLevelAndalucia);
            }
            formData.append('catNacional', values.catNacional);
            if (values.catNacional && values.riskLevelNacional) {
                formData.append('riskLevelNacional', values.riskLevelNacional);
            }
            if (values.infoPage && values.infoPage !== '') {
                formData.append('infoPage', values.infoPage);
            }
            if (values.document) {
                formData.append('document', values.document);
            }
            const response = await (await (new SpecieService()).updateSpecie(formData)).getResponseData();
            if (response.success) {
                refetch();
                onClose();
                toast.success('Especie actualizada correctamente');
            } else {
                toast.error('Error actualizando especie:', response.message);
                onClose();
                console.error('Error updating specie:', response.message);
            }
        }
        catch (error) {
            console.error('Error updating species:', error);
        } finally {
            setLoading(false);
        }
    }
    //-------------------------------------------------------------------------------------------------------------

    // FORMIK

    const formik = useFormik({
        initialValues: {
            company: '',
            commonName: '',
            scientificName: '',
            family: '',
            specieIdentifier: '',
            acronym: '',
            censusAvailable: false,
            ofcAvailable: false,
            issueAvailable: false,
            carrionAvailable: false,
            preventiveStopsAvailable: false,
            catAndalucia: false,
            catNacional: false,
            document: null,
            infoPage: '',
            riskLevelAndalucia: null,
            riskLevelNacional: null,
        },
        validationSchema: schema,
        validateOnBlur: true,
        validateOnChange: false,
        onSubmit: (values) => {
            isEdit ? handleUpdate(values) : handleCreate(values);
        },
    });

    // USE EFFECTS

    useEffect (() => {
        if (specie) {

            formik.setValues({
                company: specie.company?.id ?? specie.companyId ?? '',
                commonName: specie.commonName,
                scientificName: specie.scientificName,
                family: specie.family,
                specieIdentifier: specie.specieIdentifier,
                acronym: specie.acronym,
                riskLevelAndalucia: specie.riskLevelAndalucia,
                riskLevelNacional: specie.riskLevelNacional,
                censusAvailable: specie.censusAvailable,
                ofcAvailable: specie.ofcAvailable,
                issueAvailable: specie.issueAvailable,
                carrionAvailable: specie.carrionAvailable,
                preventiveStopsAvailable: specie.preventiveStopsAvailable,
                catAndalucia: specie.catAndalucia,
                catNacional: specie.catNacional,
                infoPage: specie.infoPage,
                document: null,
            });
        } else {
            formik.resetForm();
        }
    }, [specie, isOpen]);

    // RENDER 
    
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size='sm'
            overlayClassName='backdrop-blur'
            containerClassName='!max-w-xl !shadow-xl'
            className='z-[9999] [&_.pointer-events-none]:overflow-visible'
        >
            <div className='flex items-center justify-between p-4'>
                <h2 className='text-lg font-semibold text-gray-800 dark:text-white'>
                    {isEdit ? 'Actualizar especie' : 'Crear especie'}
                </h2>
                <button onClick={onClose} className='text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            <div className='m-auto px-7 pt-6 pb-4'>
                <form onSubmit={formik.handleSubmit}>
                    {isSuperAdmin && (
                        <CustomSelect
                            isSearchable
                            id={'company'}
                            label="Compañía"
                            options={getCompaniesList()}
                            onChange={(e: any) => {
                                formik.setFieldValue('company', e.value)
                            }}
                            value={
                                getCompaniesList().filter((company: any) => company.value === formik.values.company)
                            }
                            required
                            className="mb-4"
                            error={formik.errors.company && formik.touched.company ? (formik.errors.company as any) : undefined}
                        />
                    )}
                    <RequiredInput
                        id="commonName"
                        name="commonName"
                        label="Nombre Común"
                        placeholder="Nombre común"
                        className="mb-4"
                        formik={formik}

                    />

                    <RequiredInput
                        id="scientificName"
                        name="scientificName"
                        label="Nombre Científico"
                        placeholder="Nombre científico"
                        className="mb-4"
                        formik={formik}
                        inputClassName="italic"
                    />

                    <CustomSelect
                        isSearchable
                        id={'family'}
                        label="Grupo"
                        options={speciesGroupSelector}
                        onChange={(e: any) => {
                            formik.setFieldValue('family', e.value)
                        }}
                        value={
                            speciesGroupSelector.filter((status: any) => status.value === formik.values.family)
                        }
                        required
                        className="mb-4"
                        error={formik.errors.family && formik.touched.family ? formik.errors.family : undefined}
                    />

                    <RequiredInput
                        id="specieIdentifier"
                        name="specieIdentifier"
                        label="Identificador de especie"
                        placeholder="Identificador de especie"
                        className="mb-4"
                        formik={formik}
                    />

                    <RequiredInput
                        id="acronym"
                        name="acronym"
                        label="Acrónimo"
                        placeholder="Acrónimo"
                        className="mb-4"
                        formik={formik}
                    />

                    <Input
                        id="infoPage"
                        name="infoPage"
                        label="SEO"
                        className="mb-4"
                        placeholder="SEO"
                        onChange={(e) => formik.setFieldValue('infoPage', e.target.value)}
                        value={formik.values.infoPage}
                    />

                    <Switch
                        id={'censusAvailable'}
                        checked={formik.values.censusAvailable}
                        onChange={(e => formik.setFieldValue('censusAvailable', e.target.checked))}
                        switchKnobClassName='bg-red-300'
                        labelClassName='text-sm text-gray-600 dark:text-gray-400'
                        label='Censo disponible'
                    />

                    <Switch
                        id={'ofcAvailable'}
                        checked={formik.values.ofcAvailable}
                        onChange={(e => formik.setFieldValue('ofcAvailable', e.target.checked))}
                        switchKnobClassName='bg-red-300'
                        labelClassName='text-sm text-gray-600 dark:text-gray-400'
                        label='OFC disponible'
                    />

                    <Switch
                        id={'issueAvailable'}
                        checked={formik.values.issueAvailable}
                        onChange={(e => formik.setFieldValue('issueAvailable', e.target.checked))}
                        switchKnobClassName='bg-red-300'
                        labelClassName='text-sm text-gray-600 dark:text-gray-400'
                        label='Incidencias disponibles'
                    />

                    <Switch
                        id={'carrionAvailable'}
                        checked={formik.values.carrionAvailable}
                        onChange={(e => formik.setFieldValue('carrionAvailable', e.target.checked))}
                        switchKnobClassName='bg-red-300'
                        labelClassName='text-sm text-gray-600 dark:text-gray-400'
                        label='Carroña disponible'
                    />

                    <Switch
                        id={'catAndalucia'}
                        checked={formik.values.catAndalucia}
                        onChange={(e) => {
                            const isChecked = e.target.checked;
                            formik.setFieldValue('catAndalucia', isChecked);
                            if (!isChecked) {
                                formik.setFieldValue('riskLevelAndalucia', null);
                            }
                        }}
                        switchKnobClassName='bg-red-300'
                        labelClassName='text-sm text-gray-600 dark:text-gray-400'
                        label='Especie amenazada a nivel andaluz'
                    />

                    {formik.values.catAndalucia && (
                        <CustomSelect
                            isSearchable
                            id={'riskLevelAndalucia'}
                            label="Nivel de riesgo Andalucía"
                            options={riskLevelOptions}
                            onChange={(e: any) => {
                                formik.setFieldValue('riskLevelAndalucia', e.value)
                            }}
                            value={
                                riskLevelOptions.filter((option: any) => option.value === formik.values.riskLevelAndalucia)
                            }
                            required
                            className="mb-4"
                            error={formik.errors.riskLevelAndalucia && formik.touched.riskLevelAndalucia ? formik.errors.riskLevelAndalucia : undefined}
                        />
                    )}

                    <Switch
                        id={'catNacional'}
                        checked={formik.values.catNacional}
                        onChange={(e) => {
                            const isChecked = e.target.checked;
                            formik.setFieldValue('catNacional', isChecked);
                            if (!isChecked) {
                                formik.setFieldValue('riskLevelNacional', null);
                            }
                        }}
                        switchKnobClassName='bg-red-300'
                        labelClassName='text-sm text-gray-600 dark:text-gray-400'
                        label='Especie amenazada a nivel nacional'
                    />

                    {formik.values.catNacional && (
                        <CustomSelect
                            isSearchable
                            id={'riskLevelNacional'}
                            label="Nivel de riesgo Nacional"
                            options={riskLevelOptions}
                            onChange={(e: any) => {
                                formik.setFieldValue('riskLevelNacional', e.value)
                            }}
                            value={
                                riskLevelOptions.filter((option: any) => option.value === formik.values.riskLevelNacional)
                            }
                            required
                            className="mb-4"
                            error={formik.errors.riskLevelNacional && formik.touched.riskLevelNacional ? formik.errors.riskLevelNacional : undefined}
                        />
                    )}

                    <div className='mb-1 mt-5 flex flex-row items-center gap-2'>
                        <label htmlFor="document" className='block text-sm font-medium text-gray-700 dark:text-white'>
                            Fotografía de la especie 
                            
                        </label>
                        <Tooltip
                                content={'Se recomienda una imagen de Tamaño 200x200 para una mejor visualización'}
                                placement={'top'}
                                color={'invert'}
                                size={'sm'}
                            >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-info-circle" viewBox="0 0 16 16">
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                            <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/>
                            </svg>                        
                        </Tooltip>
                    </div>
                    <div className='mb-4 mt-2'>
                        <input
                            type="file"
                            id="document"
                            name="document"
                            accept="image/*"
                            onChange={(event) => {
                                const file = event?.currentTarget?.files ? event.currentTarget.files[0] : null;
                                formik.setFieldValue("document", file);
                            }}
                            required={isEdit ? false : true} 
                            className='mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700'
                        />
                    </div>
                </form>
            </div>

            <div className='flex justify-center p-7'>
                <Button
                    onClick={() => formik.handleSubmit()}
                    color='primary'
                >
                    {isEdit ? 'Actualizar especie' : 'Crear especie'}
                </Button>
            </div>
        </Modal>
    );
};
